import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  CircularProgress,
  IconButton,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import LockIcon from "@mui/icons-material/Lock";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import SeedingIcon from "@mui/icons-material/Agriculture";
import RefreshIcon from "@mui/icons-material/Refresh";

const ChatRoomsSection = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const editRoomId = searchParams.get("edit");

  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRoom, setEditingRoom] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    slug: "",
    description: "",
    is_active: true,
    is_default: false,
    max_users: "",
    min_age: "",
    sort_order: 0,
    password: "",
    clear_password: false,
  });
  const [saving, setSaving] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [seedResult, setSeedResult] = useState(null);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    fetchRooms();
  }, []);

  // Handle URL-based edit modal
  useEffect(() => {
    if (editRoomId && rooms.length > 0) {
      const room = rooms.find((r) => r.id === parseInt(editRoomId));
      if (room) {
        handleOpenDialog(room);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editRoomId, rooms]);

  const fetchRooms = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/admin/chatrooms`
      );
      setRooms(response.data.rooms);
    } catch (err) {
      console.error("Failed to fetch rooms:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (room = null) => {
    if (room) {
      setEditingRoom(room);
      setFormData({
        name: room.name,
        slug: room.slug,
        description: room.description || "",
        is_active: room.is_active,
        is_default: room.is_default,
        max_users: room.max_users || "",
        min_age: room.min_age || "",
        sort_order: room.sort_order || 0,
        password: "",
        clear_password: false,
      });
      // Update URL to reflect editing state
      navigate(`/admin/chatroom/rooms?edit=${room.id}`, { replace: true });
    } else {
      setEditingRoom(null);
      setFormData({
        name: "",
        slug: "",
        description: "",
        is_active: true,
        is_default: false,
        max_users: "",
        min_age: "",
        sort_order: rooms.length,
        password: "",
        clear_password: false,
      });
      navigate(`/admin/chatroom/rooms?edit=new`, { replace: true });
    }
    setShowPassword(false);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingRoom(null);
    setShowPassword(false);
    // Clear edit param from URL
    navigate(`/admin/chatroom/rooms`, { replace: true });
  };

  const handleNameChange = (e) => {
    const name = e.target.value;
    setFormData({
      ...formData,
      name,
      slug: editingRoom ? formData.slug : name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, ""),
    });
  };

  const handleSave = async () => {
    if (!formData.name.trim() || !formData.slug.trim()) return;
    setSaving(true);

    try {
      const payload = {
        name: formData.name,
        slug: formData.slug,
        description: formData.description,
        is_active: formData.is_active,
        is_default: formData.is_default,
        max_users: formData.max_users ? parseInt(formData.max_users) : null,
        min_age: formData.min_age ? parseInt(formData.min_age) : null,
        sort_order: parseInt(formData.sort_order) || 0,
      };

      // Handle password: clear_password takes precedence, then new password
      if (formData.clear_password) {
        payload.clear_password = true;
      } else if (formData.password) {
        payload.password = formData.password;
      }

      if (editingRoom) {
        await axios.put(
          `${process.env.REACT_APP_API_URL}/api/admin/chatrooms/${editingRoom.id}`,
          payload
        );
      } else {
        await axios.post(
          `${process.env.REACT_APP_API_URL}/api/admin/chatrooms`,
          payload
        );
      }
      fetchRooms();
      handleCloseDialog();
    } catch (err) {
      console.error("Failed to save room:", err);
      alert(err.response?.data?.error || "Failed to save room");
    } finally {
      setSaving(false);
    }
  };

  const handleClearPassword = async (room) => {
    if (!window.confirm(`Remove password protection from "${room.name}"?`)) return;
    try {
      await axios.put(
        `${process.env.REACT_APP_API_URL}/api/admin/chatrooms/${room.id}`,
        { clear_password: true }
      );
      fetchRooms();
    } catch (err) {
      console.error("Failed to clear password:", err);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this room?")) return;

    try {
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/api/admin/chatrooms/${id}`
      );
      fetchRooms();
    } catch (err) {
      console.error("Failed to delete room:", err);
      alert(err.response?.data?.error || "Failed to delete room");
    }
  };

  const handleToggleActive = async (room) => {
    try {
      await axios.put(
        `${process.env.REACT_APP_API_URL}/api/admin/chatrooms/${room.id}`,
        { is_active: !room.is_active }
      );
      fetchRooms();
    } catch (err) {
      console.error("Failed to toggle room:", err);
    }
  };

  const handleToggleDefault = async (room) => {
    try {
      await axios.put(
        `${process.env.REACT_APP_API_URL}/api/admin/chatrooms/${room.id}`,
        { is_default: !room.is_default }
      );
      fetchRooms();
    } catch (err) {
      console.error("Failed to toggle default:", err);
    }
  };

  const handleSeed = async () => {
    if (!window.confirm("This will add default chat rooms. Existing rooms with same slugs will be skipped. Continue?")) return;
    setSeeding(true);
    setSeedResult(null);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/admin/chatrooms/seed`
      );
      setSeedResult(response.data);
      fetchRooms();
    } catch (err) {
      console.error("Failed to seed rooms:", err);
      setSeedResult({ error: "Failed to seed rooms" });
    } finally {
      setSeeding(false);
    }
  };

  const activeRooms = rooms.filter((r) => r.is_active);
  const defaultRooms = rooms.filter((r) => r.is_default);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress sx={{ color: "#fff" }} />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", mb: 2 }}>
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          <IconButton
            onClick={fetchRooms}
            disabled={loading}
            sx={{
              color: "#9e9e9e",
              "&:hover": { color: "#fff", backgroundColor: "#394867" },
            }}
            title="Refresh"
          >
            <RefreshIcon
              sx={{
                animation: loading ? "spin 1s linear infinite" : "none",
                "@keyframes spin": {
                  "0%": { transform: "rotate(0deg)" },
                  "100%": { transform: "rotate(360deg)" },
                },
              }}
            />
          </IconButton>
          <Button
            variant="outlined"
            startIcon={seeding ? <CircularProgress size={16} sx={{ color: "#ff9800" }} /> : <SeedingIcon />}
            onClick={handleSeed}
            disabled={seeding}
            sx={{
              color: "#ff9800",
              borderColor: "#ff9800",
              "&:hover": { borderColor: "#ff9800", backgroundColor: "rgba(255, 152, 0, 0.1)" },
            }}
          >
            {seeding ? "Seeding..." : "Seed Defaults"}
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            sx={{
              backgroundColor: "#541b14",
              "&:hover": { backgroundColor: "#6d231a" },
            }}
          >
            Add Room
          </Button>
        </Box>
      </Box>

      {/* Seed Result */}
      {seedResult && (
        <Paper sx={{ p: 1.5, mb: 2, backgroundColor: seedResult.error ? "#4a1515" : "#1b3a1b", borderRadius: 1, border: `1px solid ${seedResult.error ? "#ef5350" : "#4caf50"}` }}>
          <Typography variant="body2" sx={{ color: "#fff" }}>
            {seedResult.error ? seedResult.error : `Seeding complete: ${seedResult.inserted} inserted, ${seedResult.skipped} skipped (already exist)`}
          </Typography>
        </Paper>
      )}

      {/* Stats */}
      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        <Grid item xs={6} md={3}>
          <Paper sx={{ p: 1.5, backgroundColor: "#16213e", borderRadius: 1, border: "1px solid #394867", textAlign: "center" }}>
            <Typography variant="h5" sx={{ color: "#4caf50", fontWeight: 700 }}>{rooms.length}</Typography>
            <Typography variant="caption" sx={{ color: "#9e9e9e" }}>Total Rooms</Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} md={3}>
          <Paper sx={{ p: 1.5, backgroundColor: "#16213e", borderRadius: 1, border: "1px solid #394867", textAlign: "center" }}>
            <Typography variant="h5" sx={{ color: "#2196f3", fontWeight: 700 }}>{activeRooms.length}</Typography>
            <Typography variant="caption" sx={{ color: "#9e9e9e" }}>Active Rooms</Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} md={3}>
          <Paper sx={{ p: 1.5, backgroundColor: "#16213e", borderRadius: 1, border: "1px solid #394867", textAlign: "center" }}>
            <Typography variant="h5" sx={{ color: "#ff9800", fontWeight: 700 }}>{defaultRooms.length}</Typography>
            <Typography variant="caption" sx={{ color: "#9e9e9e" }}>Default Rooms</Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} md={3}>
          <Paper sx={{ p: 1.5, backgroundColor: "#16213e", borderRadius: 1, border: "1px solid #394867", textAlign: "center" }}>
            <Typography variant="h5" sx={{ color: "#9c27b0", fontWeight: 700 }}>{rooms.length - activeRooms.length}</Typography>
            <Typography variant="caption" sx={{ color: "#9e9e9e" }}>Inactive</Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Rooms Table */}
      <Paper sx={{ backgroundColor: "#16213e", borderRadius: 2, border: "1px solid #394867", overflow: "hidden" }}>
        <TableContainer sx={{ maxHeight: 500 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ backgroundColor: "#0f1729", color: "#9e9e9e", fontWeight: 600, borderBottom: "1px solid #394867", py: 1 }}>Order</TableCell>
                <TableCell sx={{ backgroundColor: "#0f1729", color: "#9e9e9e", fontWeight: 600, borderBottom: "1px solid #394867", py: 1 }}>Name</TableCell>
                <TableCell sx={{ backgroundColor: "#0f1729", color: "#9e9e9e", fontWeight: 600, borderBottom: "1px solid #394867", py: 1 }}>Slug</TableCell>
                <TableCell sx={{ backgroundColor: "#0f1729", color: "#9e9e9e", fontWeight: 600, borderBottom: "1px solid #394867", py: 1 }}>Description</TableCell>
                <TableCell sx={{ backgroundColor: "#0f1729", color: "#9e9e9e", fontWeight: 600, borderBottom: "1px solid #394867", py: 1 }}>Locked</TableCell>
                <TableCell sx={{ backgroundColor: "#0f1729", color: "#9e9e9e", fontWeight: 600, borderBottom: "1px solid #394867", py: 1 }}>Default</TableCell>
                <TableCell sx={{ backgroundColor: "#0f1729", color: "#9e9e9e", fontWeight: 600, borderBottom: "1px solid #394867", py: 1 }}>Active</TableCell>
                <TableCell sx={{ backgroundColor: "#0f1729", color: "#9e9e9e", fontWeight: 600, borderBottom: "1px solid #394867", py: 1 }}>Limits</TableCell>
                <TableCell align="center" sx={{ backgroundColor: "#0f1729", color: "#9e9e9e", fontWeight: 600, borderBottom: "1px solid #394867", py: 1 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rooms.map((room) => (
                <TableRow key={room.id} hover sx={{ "&:hover": { backgroundColor: "#1a1a2e" }, opacity: room.is_active ? 1 : 0.5 }}>
                  <TableCell sx={{ color: "#9e9e9e", borderBottom: "1px solid #394867", py: 0.75 }}>
                    {room.sort_order}
                  </TableCell>
                  <TableCell sx={{ color: "#fff", borderBottom: "1px solid #394867", py: 0.75, fontWeight: 500 }}>
                    {room.name}
                  </TableCell>
                  <TableCell sx={{ borderBottom: "1px solid #394867", py: 0.75 }}>
                    <Chip
                      label={room.slug}
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: "0.7rem",
                        backgroundColor: "#1a1a2e",
                        color: "#9e9e9e",
                        border: "1px solid #394867",
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ color: "#9e9e9e", borderBottom: "1px solid #394867", py: 0.75, maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {room.description || "-"}
                  </TableCell>
                  <TableCell sx={{ borderBottom: "1px solid #394867", py: 0.75 }}>
                    {room.password_hash ? (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <LockIcon sx={{ fontSize: 16, color: "#ff9800" }} />
                        <IconButton
                          size="small"
                          onClick={() => handleClearPassword(room)}
                          sx={{ color: "#ef5350", p: 0.25 }}
                          title="Remove password"
                        >
                          <LockOpenIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Box>
                    ) : (
                      <Typography variant="caption" sx={{ color: "#666" }}>-</Typography>
                    )}
                  </TableCell>
                  <TableCell sx={{ borderBottom: "1px solid #394867", py: 0.75 }}>
                    <Switch
                      size="small"
                      checked={room.is_default}
                      onChange={() => handleToggleDefault(room)}
                      sx={{
                        "& .MuiSwitch-switchBase.Mui-checked": { color: "#ff9800" },
                        "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: "#ff9800" },
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ borderBottom: "1px solid #394867", py: 0.75 }}>
                    <Switch
                      size="small"
                      checked={room.is_active}
                      onChange={() => handleToggleActive(room)}
                      sx={{
                        "& .MuiSwitch-switchBase.Mui-checked": { color: "#4caf50" },
                        "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: "#4caf50" },
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ color: "#9e9e9e", borderBottom: "1px solid #394867", py: 0.75, fontSize: "0.75rem" }}>
                    {room.max_users && <span>Max: {room.max_users}</span>}
                    {room.max_users && room.min_age && <span> | </span>}
                    {room.min_age && <span>Age: {room.min_age}+</span>}
                    {!room.max_users && !room.min_age && <span>-</span>}
                  </TableCell>
                  <TableCell align="center" sx={{ borderBottom: "1px solid #394867", py: 0.75 }}>
                    <IconButton size="small" onClick={() => handleOpenDialog(room)} sx={{ color: "#2196f3", p: 0.5 }}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleDelete(room.id)} sx={{ color: "#ef5350", p: 0.5 }}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {rooms.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9} sx={{ color: "#666", textAlign: "center", py: 4, borderBottom: "1px solid #394867" }}>
                    No chat rooms defined. Click "Seed Defaults" to add standard rooms.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth PaperProps={{ sx: { backgroundColor: "#16213e", color: "#fff" } }}>
        <DialogTitle>{editingRoom ? "Edit Room" : "Add New Room"}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} md={6}>
              <TextField
                autoFocus
                label="Room Name"
                fullWidth
                value={formData.name}
                onChange={handleNameChange}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    color: "#fff",
                    "& fieldset": { borderColor: "#394867" },
                    "&:hover fieldset": { borderColor: "#541b14" },
                    "&.Mui-focused fieldset": { borderColor: "#541b14" },
                  },
                  "& .MuiInputLabel-root": { color: "#9e9e9e" },
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Slug (URL path)"
                fullWidth
                value={formData.slug}
                onChange={(e) => setFormData({ ...formData, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "") })}
                helperText="Lowercase letters, numbers, hyphens only"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    color: "#fff",
                    "& fieldset": { borderColor: "#394867" },
                    "&:hover fieldset": { borderColor: "#541b14" },
                    "&.Mui-focused fieldset": { borderColor: "#541b14" },
                  },
                  "& .MuiInputLabel-root": { color: "#9e9e9e" },
                  "& .MuiFormHelperText-root": { color: "#666" },
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Description"
                fullWidth
                multiline
                rows={2}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    color: "#fff",
                    "& fieldset": { borderColor: "#394867" },
                    "&:hover fieldset": { borderColor: "#541b14" },
                    "&.Mui-focused fieldset": { borderColor: "#541b14" },
                  },
                  "& .MuiInputLabel-root": { color: "#9e9e9e" },
                }}
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField
                label="Sort Order"
                type="number"
                fullWidth
                value={formData.sort_order}
                onChange={(e) => setFormData({ ...formData, sort_order: e.target.value })}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    color: "#fff",
                    "& fieldset": { borderColor: "#394867" },
                    "&:hover fieldset": { borderColor: "#541b14" },
                    "&.Mui-focused fieldset": { borderColor: "#541b14" },
                  },
                  "& .MuiInputLabel-root": { color: "#9e9e9e" },
                }}
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField
                label="Max Users"
                type="number"
                fullWidth
                value={formData.max_users}
                onChange={(e) => setFormData({ ...formData, max_users: e.target.value })}
                placeholder="Unlimited"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    color: "#fff",
                    "& fieldset": { borderColor: "#394867" },
                    "&:hover fieldset": { borderColor: "#541b14" },
                    "&.Mui-focused fieldset": { borderColor: "#541b14" },
                  },
                  "& .MuiInputLabel-root": { color: "#9e9e9e" },
                }}
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField
                label="Min Age"
                type="number"
                fullWidth
                value={formData.min_age}
                onChange={(e) => setFormData({ ...formData, min_age: e.target.value })}
                placeholder="None"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    color: "#fff",
                    "& fieldset": { borderColor: "#394867" },
                    "&:hover fieldset": { borderColor: "#541b14" },
                    "&.Mui-focused fieldset": { borderColor: "#541b14" },
                  },
                  "& .MuiInputLabel-root": { color: "#9e9e9e" },
                }}
              />
            </Grid>
            {/* Password Protection Section */}
            <Grid item xs={12}>
              <Paper sx={{ p: 2, backgroundColor: "#1a1a2e", borderRadius: 1, border: `1px solid ${editingRoom?.password_hash ? "#ff9800" : "#394867"}` }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                  {editingRoom?.password_hash ? (
                    <LockIcon sx={{ color: "#ff9800", fontSize: 20 }} />
                  ) : (
                    <LockOpenIcon sx={{ color: "#666", fontSize: 20 }} />
                  )}
                  <Typography variant="subtitle2" sx={{ color: "#fff", fontWeight: 600 }}>
                    Password Protection
                  </Typography>
                  <Chip
                    label={editingRoom?.password_hash ? "LOCKED" : "UNLOCKED"}
                    size="small"
                    sx={{
                      ml: "auto",
                      height: 20,
                      fontSize: "0.65rem",
                      fontWeight: 600,
                      backgroundColor: editingRoom?.password_hash ? "rgba(255, 152, 0, 0.2)" : "rgba(102, 102, 102, 0.2)",
                      color: editingRoom?.password_hash ? "#ff9800" : "#666",
                      border: `1px solid ${editingRoom?.password_hash ? "#ff9800" : "#666"}`,
                    }}
                  />
                </Box>

                {editingRoom?.password_hash ? (
                  <Box>
                    <Typography variant="caption" sx={{ color: "#9e9e9e", display: "block", mb: 1.5 }}>
                      This room requires a password to enter. You cannot view the current password, but you can set a new one or remove it.
                    </Typography>
                    <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
                      <TextField
                        label="Set New Password"
                        type={showPassword ? "text" : "password"}
                        size="small"
                        value={formData.password}
                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                        placeholder="Enter new password..."
                        sx={{
                          flex: 1,
                          "& .MuiOutlinedInput-root": {
                            color: "#fff",
                            "& fieldset": { borderColor: "#394867" },
                            "&:hover fieldset": { borderColor: "#541b14" },
                            "&.Mui-focused fieldset": { borderColor: "#541b14" },
                          },
                          "& .MuiInputLabel-root": { color: "#9e9e9e" },
                        }}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                onClick={() => setShowPassword(!showPassword)}
                                edge="end"
                                size="small"
                                sx={{ color: "#9e9e9e" }}
                              >
                                {showPassword ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<LockOpenIcon />}
                        onClick={() => setFormData({ ...formData, clear_password: true })}
                        disabled={formData.clear_password}
                        sx={{
                          color: formData.clear_password ? "#4caf50" : "#ef5350",
                          borderColor: formData.clear_password ? "#4caf50" : "#ef5350",
                          whiteSpace: "nowrap",
                          "&:hover": {
                            borderColor: formData.clear_password ? "#4caf50" : "#ef5350",
                            backgroundColor: formData.clear_password ? "rgba(76, 175, 80, 0.1)" : "rgba(239, 83, 80, 0.1)",
                          },
                        }}
                      >
                        {formData.clear_password ? "Will Remove" : "Remove Password"}
                      </Button>
                    </Box>
                    {formData.clear_password && (
                      <Box sx={{ mt: 1, display: "flex", alignItems: "center", gap: 1 }}>
                        <Typography variant="caption" sx={{ color: "#4caf50" }}>
                          Password will be removed when you save.
                        </Typography>
                        <Button
                          size="small"
                          onClick={() => setFormData({ ...formData, clear_password: false })}
                          sx={{ color: "#9e9e9e", fontSize: "0.7rem", minWidth: "auto", p: 0.5 }}
                        >
                          Undo
                        </Button>
                      </Box>
                    )}
                    {formData.password && !formData.clear_password && (
                      <Typography variant="caption" sx={{ color: "#ff9800", display: "block", mt: 1 }}>
                        Password will be changed when you save.
                      </Typography>
                    )}
                  </Box>
                ) : (
                  <Box>
                    <Typography variant="caption" sx={{ color: "#9e9e9e", display: "block", mb: 1.5 }}>
                      This room is open to everyone. Set a password to restrict access.
                    </Typography>
                    <TextField
                      label="Set Password (optional)"
                      type={showPassword ? "text" : "password"}
                      size="small"
                      fullWidth
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      placeholder="Leave empty to keep room open..."
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          color: "#fff",
                          "& fieldset": { borderColor: "#394867" },
                          "&:hover fieldset": { borderColor: "#541b14" },
                          "&.Mui-focused fieldset": { borderColor: "#541b14" },
                        },
                        "& .MuiInputLabel-root": { color: "#9e9e9e" },
                      }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setShowPassword(!showPassword)}
                              edge="end"
                              size="small"
                              sx={{ color: "#9e9e9e" }}
                            >
                              {showPassword ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                    {formData.password && (
                      <Typography variant="caption" sx={{ color: "#ff9800", display: "block", mt: 1 }}>
                        Room will be locked with this password when you save.
                      </Typography>
                    )}
                  </Box>
                )}
              </Paper>
            </Grid>
            <Grid item xs={6} md={3}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <Typography variant="body2" sx={{ color: "#9e9e9e" }}>Active</Typography>
                  <Switch
                    size="small"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    sx={{
                      "& .MuiSwitch-switchBase.Mui-checked": { color: "#4caf50" },
                      "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: "#4caf50" },
                    }}
                  />
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <Typography variant="body2" sx={{ color: "#9e9e9e" }}>Default</Typography>
                  <Switch
                    size="small"
                    checked={formData.is_default}
                    onChange={(e) => setFormData({ ...formData, is_default: e.target.checked })}
                    sx={{
                      "& .MuiSwitch-switchBase.Mui-checked": { color: "#ff9800" },
                      "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: "#ff9800" },
                    }}
                  />
                </Box>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} sx={{ color: "#9e9e9e" }}>Cancel</Button>
          <Button
            onClick={handleSave}
            disabled={saving || !formData.name.trim() || !formData.slug.trim()}
            sx={{ color: "#4caf50" }}
          >
            {saving ? <CircularProgress size={20} /> : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default ChatRoomsSection;
